import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {FormComponent} from 'appShared/widgets';
import {FormInterface} from 'appShared/interfaces';
import {
    DialogActionsEnum,
    DialogButtonInterface,
    DialogButtonTypesEnum,
    DialogConfigInterface,
    SelectOptionInterface
} from 'appShared/components';

import {Router} from '@angular/router';

import {PanelPaths} from 'appShared/paths';
import {AlertsService} from 'appShared/services';
import {Subscription} from 'rxjs';
import {AbstractControl, Validators} from '@angular/forms';
import {FundOrderMessages} from 'appAssets/messages';
import {Helper} from 'appShared/classes';

import {
    FundOrderMatchingInventoryAmountSelector,
    FundOrderMatchingInventoryCounterpartySelector,
    FundOrderMatchingInventoryDateSelector,
    FundOrderMatchingInventoryForm,
    FundOrderMatchingInventoryInterestSelector,
    FundOrderMatchingInventoryPriceSelector,
    FundOrderMatchingInventoryProductSelector,
    FundOrderMatchingInventoryTimeSelector,
} from 'appEntries/panel/forms';

import {
    AccountBalanceResponseInterface,
    AccountSubTypeEnum,
    CashAccountDetailedReportModel,
    CurrencyTypesEnum,
    FundInventoryOrderInterface,
    FundOrderReportInterface,
    FundOrdersService,
    FundOrderTypesEnum,
    FundProductInterface,
    FundProductTypesEnum,
    OperatorInterface,
    ProductCategory,
    ServerResponse,
    TradingAccountDetailedReportModel
} from '@spa/api';

import {FundCreateOrderComponent, GetFundOrderBuyAmountPlaceholderText} from 'appAssets/descriptors';
import * as moment from "moment";

@Component({
    selector: 'app-fund-order-buy-matching-inventory',
    templateUrl: './fund-order-matching-inventory.component.html',
    styleUrls: ['./fund-order-matching-inventory.component.scss']
})
export class FundOrderMatchingInventoryComponent extends FundCreateOrderComponent<FundOrderMatchingInventoryComponent> implements OnInit, OnDestroy, FormInterface {
    private readonly panelPaths: typeof PanelPaths;
    private readonly products: FundProductInterface[];
    public readonly operator: OperatorInterface;

    private amountControl: AbstractControl;
    private productControl: AbstractControl;
    private priceControl: AbstractControl;
    private interestControl: AbstractControl;

    public readonly isMutualFundAccount: boolean;

    public orderForm: FundOrderMatchingInventoryForm;
    public account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;

    public calculatedAmount: string;
    public accountCashCurrencySign: string;
    public productCurrencySign: string;
    public showProductPriceSection: boolean;
    public productPriceBoxText: string;
    public productPriceBoxWarningText: string;
    public productPriceDate: string;
    public productPriceAmount: number;
    public productPriceInterest: number;
    public amountNotValidBoxText: string;

    constructor(
        public readonly ordersService: FundOrdersService,
        public readonly router: Router,
        public readonly alertsService: AlertsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
            products: FundProductInterface[],
            operator: OperatorInterface,
            accountBalance: AccountBalanceResponseInterface,
            availableCounterParties: string[]
        },
        public dialogRef: MatDialogRef<FundOrderMatchingInventoryComponent>,
    ) {
        super(data.account, data.operator, ordersService, alertsService, router, dialogRef);

        this.orderType = FundOrderTypesEnum.Buy;

        this.config = data.config;
        this.account = data.account;
        this.products = data.products.filter(p => p.buyPrice);
        this.operator = data.operator;
        this.panelPaths = PanelPaths;

        this.isMutualFundAccount = this.data.account
            ? this.data.account.AccountSubType === AccountSubTypeEnum.MutualFund : false;

        this.calculatedAmount = '0';
        this.showProductPriceSection = false;
        this.productPriceAmount = 0;
        this.productPriceInterest = 0;
        this.amountNotValidBoxText = '';
        this.model.cash = data.accountBalance;
    }

    private initFormControls(): void {
        this.amountControl = this.orderForm.form.get(FundOrderMatchingInventoryAmountSelector);
        this.productControl = this.orderForm.form.get(FundOrderMatchingInventoryProductSelector);
        this.priceControl = this.orderForm.form.get(FundOrderMatchingInventoryPriceSelector);
        this.interestControl = this.orderForm.form.get(FundOrderMatchingInventoryInterestSelector);

        this.orderForm.controls.amount.placeholder = GetFundOrderBuyAmountPlaceholderText(this.account.AccountSubType);

        switch (this.account.AccountSubType) {
            case AccountSubTypeEnum.MutualFund:
                this.showProductPriceSection = false;

                break;

            case AccountSubTypeEnum.InstrumentCertificate:
                this.showProductPriceSection = true;
                this.productPriceBoxText = FundOrderMessages.FundOrderBuyProductPriceBoxText.message;

                break;
        }

        if (this.productControl) {
            const subscription = this.productControl.valueChanges.subscribe(value => this.handleProductControlOnChange(value));
            this.orderSubscriptions.push(subscription);
        }

        if (this.amountControl) {
            const subscription = this.amountControl.valueChanges.subscribe(value => this.onValueChanged(value));
            this.orderSubscriptions.push(subscription);
        }

        if (this.priceControl) {
            const subscription = this.priceControl.valueChanges.subscribe(value => this.onPriceChanged(value));
            this.orderSubscriptions.push(subscription);
        }

        if (this.interestControl) {
            const subscription = this.interestControl.valueChanges.subscribe(value => this.onInterestChanged(value));
            this.orderSubscriptions.push(subscription);
        }

        this.orderForm.controls.product.options = this.initProductsControlOptions(this.products);

        this.orderForm.controls.counterparty.options = this.initCounterPartyControlOptions();

        this.setAmountControlValidators();
    }

    private setAmountControlValidators(): void {
        if (this.amountControl) {
            switch (this.account.AccountSubType) {
                case AccountSubTypeEnum.MutualFund:
                    break;
                case AccountSubTypeEnum.InstrumentCertificate:
                    this.amountControl.setValidators([Validators.required]);
                    break;
                default:
                    this.amountControl.setValidators([Validators.required]);
                    break;
            }

            this.amountControl.updateValueAndValidity();
        }
    }

    private initProductsControlOptions(products: FundProductInterface[]): SelectOptionInterface[] {
        return products.map((product: FundProductInterface) => ({
            label: `${product.name} - ${product.code}`,
            value: product.code
        }));
    }

    private initCounterPartyControlOptions(): SelectOptionInterface[] {
        return this.data.availableCounterParties.map(p => ({
            label: p,
            value: p
        }));
    }

    private handleProductControlOnChange(value: string): void {
        const product = this.products.find(item => item.code === value);

        if (!product) {
            this.model.product = null;

            return;
        }

        super.init(product, null, this.data.accountBalance);
        this.cleanFormData('');
        this.productCurrencySign = Helper.getCurrencySign(this.model.product.currency);

        if (product.category === ProductCategory.FixedRateBonds) {
            this.interestControl.setValidators([Validators.required])
            this.interestControl.updateValueAndValidity();
        }
        else {
            this.interestControl.setValidators([])
            this.interestControl.updateValueAndValidity();
        }
    }

    onValueChanged(amount: string | null): void {
        if (amount) {
            this.model.inputValue = +Helper.compactString(amount);
        } else {
            this.model.inputValue = 0;
        }

        if (this.model.product) {
            this.calculateBuy();
        }
    }

    onInterestChanged(amount: string | null): void {
        if (amount) {
            this.productPriceInterest = +Helper.compactString(amount);
        } else {
            this.productPriceInterest = 0;
        }

        if (this.model.product) {
            this.calculateBuy();
        }
    }

    onPriceChanged(amount: string | null): void {
        if (amount) {
            this.productPriceAmount = +Helper.compactString(amount);
        } else {
            this.productPriceAmount = 0;
        }

        if (this.model.product) {
            this.calculateBuy();
        }
    }

    private calculateBuy(): void {
        const cash = +this.model.cash;

        this.model.totals.warning = false;

        if (cash === 0) {
            this.model.totals.insufficient = true;
        }

        let amount = 0;
        let quantity = 0;
        let fee = 0;
        let interest = 0;
        let price = 0;
        let cleanPrice = 0;

        const inputValue = this.model.inputValue;
        const buyPriceValue = this.getPrice();
        const buyPriceInterest = this.getInterest();

        switch (this.model.product.type) {
            case FundProductTypesEnum.MutualFund:
                amount = inputValue;
                quantity = amount / buyPriceValue;

                if (inputValue && amount < this.model.product.minimumBuyOrderSize) {
                    this.model.totals.warning = true;
                }

                break;
            case FundProductTypesEnum.InvestmentCertificate:
                amount = inputValue * buyPriceValue;
                quantity = inputValue;

                break;
            case FundProductTypesEnum.Bonds:
                amount = inputValue * buyPriceValue / 100;
                quantity = inputValue / this.model.product.nominalValue;
                cleanPrice = price = buyPriceValue;

                if (inputValue % this.model.product.minimumBuyOrderSize !== 0) {
                    this.model.totals.warning = true;
                }

                if (this.isCouponBonds && buyPriceInterest > 0) {
                    interest = inputValue * buyPriceInterest / 100;
                    amount += interest;
                    price = buyPriceValue + buyPriceInterest;
                }

                fee = this.getFee(amount);

                break;
        }

        this.model.totals.insufficient = (amount + fee) > cash;
        this.model.totals.amount = Math.round((amount + Number.EPSILON) * 10000) / 10000
        this.model.totals.quantity = +quantity;
        this.model.totals.fee = +fee;
        this.model.totals.interest = +interest;
        this.model.totals.price = +price;
        this.model.totals.cleanPrice = +cleanPrice;
    }

    private cleanFormData(warningBoxText: string): void {
        this.productPriceAmount = 0;
        this.productPriceBoxText = FundOrderMessages.FundOrderBuyProductPriceBoxText.message;
        this.amountNotValidBoxText = '';
        this.amountControl.setValue('');
        this.priceControl.setValue('');
        this.interestControl.setValue('');
        this.setProductPriceWarningText(warningBoxText);

        const submitButton = this.config.buttons.find((button: DialogButtonInterface) => button.type === DialogButtonTypesEnum.Confirm);

        if (submitButton) {
            submitButton.disabled = warningBoxText.trim() !== '';
        }
    }

    private setProductPriceWarningText(text: string): void {
        this.productPriceBoxWarningText = text;
    }

    ngOnInit(): void {
        this.initForm();

        this.accountCashCurrencySign = Helper.getCurrencySign(CurrencyTypesEnum.Euro);
        this.productCurrencySign = Helper.getCurrencySign(CurrencyTypesEnum.Euro);
    }

    ngOnDestroy(): void {
        this.orderSubscriptions.forEach((subscription: Subscription) => subscription && subscription.unsubscribe());
    }

    initForm(): void {
        this.orderForm = new FundOrderMatchingInventoryForm();

        this.orderForm.initForm();

        this.initFormControls();
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    validateForm(): boolean {
        let timeValidate = true;
        if(!this.getExecutionCreatedOn()) {
            this.orderForm.controls.executionCreatedOn.setError('The execution time format is invalid.');
            timeValidate = false;
        }

        return [
            timeValidate,
            !this.model.invalid,
            (this.amountNotValidBoxText.trim() === ''),
            this.orderForm.validateForm(this.getFormMessages)
        ].indexOf(false) < 0;
    }

    private parseDateAndTimeString(value: string): (Date | null) {
        const parsed = moment(value, moment.ISO_8601, true);

        if (parsed.isValid()) {
            return parsed.toDate();
        }

        return null;
    }

    private getExecutionCreatedOn(): (Date | null) {
        const executionCreatedOnDate = this.orderForm.form.value[FundOrderMatchingInventoryDateSelector];
        const executionCreatedOnTime = this.orderForm.form.value[FundOrderMatchingInventoryTimeSelector];

        if (!executionCreatedOnTime) {
            return null;
        }

        const formattedDate = moment(executionCreatedOnDate).format('YYYY-MM-DD');
        const mergedDate = formattedDate + 'T' + executionCreatedOnTime;
        return this.parseDateAndTimeString(mergedDate);
    }

    confirmOrderMethod(): ServerResponse<FundOrderReportInterface> {
        const counterParty = this.orderForm.form.value[FundOrderMatchingInventoryCounterpartySelector];
        const executionCreatedOn = this.getExecutionCreatedOn();
        const amount = +this.model.totals.amount;
        const quantity = +this.model.totals.quantity;
        const fee = +this.model.totals.fee;
        const interest = +this.model.totals.interest;
        const price = +this.model.totals.price;
        const cleanPrice = +this.model.totals.cleanPrice;

        const requestModel: FundInventoryOrderInterface =
            {
                ...this.getRequestModel(),
                amount,
                cleanPrice,
                fee,
                interest,
                price,
                quantity,
                executionCreatedOn: executionCreatedOn,
                counterParty: counterParty
            };

        return this.ordersService.postInventoryOrder(requestModel);
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.startLoading(() => {
                        this.confirmOrder();
                    });
                }
                break;
        }
    }

    getPrice(): number {
        return this.productPriceAmount;
    }

    getInterest(): number {
        return this.productPriceInterest;
    }

    getFee(amount: number): number {
        return 0;
    }
}

